import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { Package, FolderOpen, ClipboardList, TrendingUp, Users, ArrowUpRight, ArrowDownRight, Euro } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [appVersion, setAppVersion] = useState('');
  const [visitorStats, setVisitorStats] = useState(null);

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data)).catch(() => {});
    api.get('/site-settings').then(r => setAppVersion(r.data.app_version || '')).catch(() => {});
    api.get('/visitor-stats').then(r => setVisitorStats(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-[#0E518D] border-t-transparent rounded-full" /></div>;

  const stats = [
    { label: 'Produkte', value: data.products.total, sub: `${data.products.active} aktiv`, icon: Package, color: 'bg-[#0E518D]' },
    { label: 'Kategorien', value: data.categories.total, sub: '', icon: FolderOpen, color: 'bg-[#D4AF37]' },
    { label: 'Bestellungen', value: data.orders.total, sub: `${data.orders.new} neu`, icon: ClipboardList, color: 'bg-green-600' },
    { label: 'In Bearbeitung', value: data.orders.in_progress, sub: '', icon: TrendingUp, color: 'bg-orange-500' },
  ];

  // Monatsnamen für die Anzeige
  const getMonthName = (monthKey) => {
    if (!monthKey) return '';
    const [year, month] = monthKey.split('-');
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0E518D]">Dashboard</h1>
        <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-sm" data-testid="dashboard-version">Version: {appVersion}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s, i) => (
          <Card key={i} className="bg-white rounded-sm shadow-sm border border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{s.label}</p>
                  <p className="text-3xl font-bold text-[#1E293B] mt-1">{s.value}</p>
                  {s.sub && <p className="text-xs text-slate-400 mt-1">{s.sub}</p>}
                </div>
                <div className={`w-11 h-11 ${s.color} rounded-sm flex items-center justify-center`}>
                  <s.icon className="h-5 w-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Besucher-Statistik */}
      {visitorStats && (
        <div className="mb-8">
          <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Eindeutige Besucher (Monat)</p>
                  <div className="flex items-baseline gap-3 mt-1">
                    <p className="text-3xl font-bold text-[#1E293B]">{visitorStats.current_count}</p>
                    {visitorStats.change_percent !== 0 && (
                      <span className={`flex items-center text-sm font-medium ${visitorStats.change_percent > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {visitorStats.change_percent > 0 ? (
                          <ArrowUpRight className="h-4 w-4" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4" />
                        )}
                        {visitorStats.change_percent > 0 ? '+' : ''}{visitorStats.change_percent}%
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {getMonthName(visitorStats.current_month)} • Vormonat: {visitorStats.prev_count} ({getMonthName(visitorStats.prev_month)})
                  </p>
                </div>
                <div className="w-11 h-11 bg-purple-500 rounded-sm flex items-center justify-center">
                  <Users className="h-5 w-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Gewinn laufendes Jahr - Detaillierte Aufschlüsselung */}
      {data.profit && (
        <div className="mb-8">
          <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm text-slate-500">Gewinn laufendes Jahr ({data.profit.year})</p>
                  <div className="flex items-baseline gap-3 mt-1">
                    <p className="text-3xl font-bold text-green-600" data-testid="profit-total">{data.profit.total?.toFixed(2)} EUR</p>
                  </div>
                  {/* Detaillierte Aufschlüsselung */}
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Produktgewinn:</span>
                      <span className="font-medium text-slate-700" data-testid="profit-products">{data.profit.product_profit?.toFixed(2)} EUR</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Liefer- & Servicepauschale:</span>
                      <span className="font-medium text-slate-700" data-testid="profit-service-fee">{data.profit.service_fee_profit?.toFixed(2)} EUR</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Aus {data.profit.orders_count} Bestellungen (ohne Stornierungen)
                  </p>
                </div>
                <div className="w-11 h-11 bg-green-500 rounded-sm flex items-center justify-center ml-4">
                  <Euro className="h-5 w-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Letzte Bestellungen mit Gewinn</CardTitle></CardHeader>
          <CardContent>
            {data.recent_orders.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Keine Bestellungen vorhanden</p>
            ) : (
              <div className="space-y-3">
                {data.recent_orders.map(o => (
                  <div key={o.id} className="bg-slate-50 rounded-sm p-3" data-testid={`recent-order-${o.id}`}>
                    {/* Kopfzeile: Bestellnummer, Name, Status */}
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-[#1E293B]">{o.order_number}</p>
                        <p className="text-xs text-slate-400">{o.first_name} {o.last_name} - {formatDate(o.arrival_date)}</p>
                      </div>
                      <Badge variant="outline" className={`text-xs ${o.status === 'new' ? 'border-green-500 text-green-600' : o.status === 'in_progress' ? 'border-orange-500 text-orange-600' : o.status === 'cancelled' ? 'border-red-500 text-red-600' : 'border-slate-300 text-slate-500'}`}>
                        {o.status === 'new' ? 'Neu' : o.status === 'in_progress' ? 'In Bearbeitung' : o.status === 'cancelled' ? 'Storniert' : 'Erledigt'}
                      </Badge>
                    </div>
                    
                    {/* Gewinn-Details (nur für nicht-stornierte Bestellungen) */}
                    {o.status !== 'cancelled' ? (
                      <div className="border-t border-slate-200 pt-2 mt-2">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Bestellwert:</span>
                            <span className="font-medium text-[#0E518D]">{o.total?.toFixed(2)} EUR</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Produktgewinn:</span>
                            <span className="font-medium text-slate-700">{o.product_profit?.toFixed(2)} EUR</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Servicepauschale:</span>
                            <span className="font-medium text-slate-700">{o.service_fee_profit?.toFixed(2)} EUR</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 font-medium">Gesamtgewinn:</span>
                            <span className="font-bold text-green-600">{o.total_profit?.toFixed(2)} EUR</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="border-t border-slate-200 pt-2 mt-2">
                        <p className="text-xs text-red-500 italic">Storniert - Kein Gewinn</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Beliebte Produkte</CardTitle></CardHeader>
          <CardContent>
            {data.popular_products.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Keine Produkte vorhanden</p>
            ) : (
              <div className="space-y-3">
                {data.popular_products.map(p => (
                  <div key={p.id} className="flex items-center justify-between bg-slate-50 rounded-sm p-3" data-testid={`popular-product-${p.id}`}>
                    <div>
                      <p className="text-sm font-medium text-[#1E293B]">{p.name}</p>
                      <p className="text-xs text-slate-400">Art. {p.article_number}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-[#0E518D]">{p.price?.toFixed(2)} EUR</p>
                      <p className="text-xs text-slate-400">{p.auto_order_count || 0} Bestellungen</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
